import styled, { css } from 'styled-components';

export const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 24px;
  @media (max-width: 640px) {
    padding: 16px;
  }
`;

export const HeaderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
`;

export const Subtle = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.palette[700]};
`;

export const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`;

export const Segmented = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  max-width: 100%;
  border: 1px solid ${({ theme }) => theme.palette[400]};
  border-radius: 8px;
  overflow: hidden;
`;

export const SegmentButton = styled.button<{ $active?: boolean }>`
  padding: 7px 12px;
  font-size: 13px;
  border: none;
  cursor: pointer;
  background: ${({ theme, $active }) => ($active ? theme.primary[500] : theme.palette[10])};
  color: ${({ theme, $active }) => ($active ? '#FFFFFF' : theme.palette[850])};
  & + & {
    border-left: 1px solid ${({ theme }) => theme.palette[400]};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.primary[400]};
    outline-offset: -2px;
  }
`;

export const RefreshButton = styled.button`
  padding: 7px 14px;
  font-size: 13px;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.primary[500]};
  background: transparent;
  color: ${({ theme }) => (theme.isDark ? theme.primary[400] : theme.primary[500])};
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const Banner = styled.div<{ $tone: 'warning' | 'info' }>`
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  line-height: 1.5;
  ${({ $tone, theme }) =>
    $tone === 'warning'
      ? css`
          background: ${theme.isDark ? 'rgba(232, 80, 72, 0.15)' : theme.errors[100]};
          color: ${theme.isDark ? theme.errors[200] : theme.errors[600]};
          border: 1px solid ${theme.errors[600]};
        `
      : css`
          background: ${theme.isDark ? 'rgba(15, 104, 233, 0.12)' : theme.primary[50]};
          color: ${theme.palette[850]};
          border: 1px solid ${theme.isDark ? theme.palette[400] : theme.primary[100]};
        `}
`;

export const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(220px, 100%), 1fr));
  gap: 12px;
`;

export const Card = styled.section`
  background: ${({ theme }) => theme.palette[10]};
  border: 1px solid ${({ theme }) => theme.palette[300]};
  border-radius: 10px;
  padding: 16px;
  min-width: 0;
  box-sizing: border-box;
`;

export const KpiCard = styled(Card)<{ $tone?: 'warning' }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  ${({ $tone }) =>
    $tone === 'warning' &&
    css`
      border-color: #d99a1e;
      border-left-width: 4px;
    `}
`;

export const KpiLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${({ theme }) => theme.palette[700]};
`;

export const KpiValue = styled.div`
  font-size: 22px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.palette[900]};
  overflow-wrap: anywhere;
`;

export const KpiSecondary = styled.div`
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.palette[700]};
`;

export const Delta = styled.span<{ $direction: 'up' | 'down' | 'flat'; $goodWhenUp?: boolean }>`
  font-size: 12px;
  font-weight: 600;
  color: ${({ $direction, $goodWhenUp = true, theme }) =>
    $direction === 'flat'
      ? theme.palette[700]
      : ($direction === 'up') === $goodWhenUp
        ? theme.success
        : theme.errors[600]};
`;

export const InfoDot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 700;
  cursor: help;
  color: #ffffff;
  background: #d99a1e;
`;

export const ChartGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 4px;
  color: ${({ theme }) => theme.palette[900]};
`;

export const ChartBox = styled.div`
  position: relative;
  height: 280px;
  margin-top: 12px;
`;

export const Empty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  font-size: 13px;
  color: ${({ theme }) => theme.palette[700]};
`;

export const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
`;

export const TwoCol = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
  gap: 16px;
  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const RegionTag = styled.span`
  display: inline-block;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ theme }) => theme.palette[300]};
  color: ${({ theme }) => theme.palette[850]};
`;

export const Muted = styled.span`
  color: ${({ theme }) => theme.palette[700]};
  font-size: 12px;
`;
